import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { levelLabel } from '../pages/PersonCard.js';
import { durationTH, thaiDate } from './Warnings.js';
import type { Position } from '@shared/types';
import type { PersonProfile } from '@shared/views';

/**
 * ผู้ครองปัจจุบัน — อ่านเป็น "เส้นทางราชการ" แกนเดียว
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมไม่ใช่ตารางสองคอลัมน์แบบเดิม
 * ────────────────────────────────────────────────────────────────
 *
 * ตารางให้น้ำหนักทุกช่องเท่ากันหมด — "วันเกษียณ" กับ "ปีงบประมาณที่เกษียณ"
 * ดูสำคัญเท่ากัน ทั้งที่อันแรกคือสิ่งที่ต้องวางแผน อีกอันเป็นเลขอ้างอิง
 *
 * แกนนี้เรียงจาก **อนาคตลงมาหาอดีต** โดยตั้งใจ — บรรทัดบนสุดคือวันที่อัตรานี้จะว่าง
 * ซึ่งเป็นคำถามที่งานวางแผนอัตรากำลังถามก่อนเสมอ ไม่ใช่ "เขาเข้ามาเมื่อไร"
 *
 * ⚠ หัว (เกษียณ) กับท้าย (เข้ารับราชการ) อยู่นอกกล่องที่เลื่อน ส่วนช่วงกลางเลื่อนได้
 *   ปลายทั้งสองข้างคือกรอบเวลาของคนคนนี้ ถ้าเลื่อนหายไปด้วย ผู้อ่านจะไม่เหลือหลักอ้างอิง
 *   ว่ากำลังดูช่วงไหนของชีวิตราชการเขาอยู่
 */

/** เกินกี่รายการถึงให้ช่วงกลางเลื่อนแทนการยืดยาวลงไปเรื่อย ๆ */
const SCROLL_AFTER = 5;

type Period = PersonProfile['careerHistory']['items'][number];

export function IncumbentCard({ p }: { p: Position }) {
  const who = p.incumbent;
  const id = who?.personaId ?? null;

  /* ⚠ queryKey ต้องตรงกับของ PersonCard เป๊ะ ๆ — สองที่นี้อ่านโปรไฟล์ก้อนเดียวกัน
     ถ้าคีย์ต่างกัน การเปิดอัตราหนึ่งครั้งจะยิงคำขอเดิมสองรอบโดยไม่มีใครสังเกต */
  const q = useQuery({
    queryKey: ['profile', id],
    queryFn: () => api.profile(id as string),
    enabled: id !== null,
  });

  if (!who) return null;

  const items: Period[] = q.data?.careerHistory.items ?? [];
  const scrolls = items.length > SCROLL_AFTER;

  return (
    <div className="inc">
      {/* ────────── แถบตัวตน ────────── */}
      {/* ⚠ ไม่มีอักษรย่อหน้าชื่อแล้ว — ผู้ใช้สั่งเอาออก และเป็นการเอาออกที่ถูกต้อง
          ตัวอักษรตัวเดียวจากชื่อไทยแยกคนไม่ได้จริง (กระทรวงนี้มีคนขึ้นต้นด้วย "ส"
          อยู่หลายร้อยคน) มันจึงเป็นแค่สีสันที่กินพื้นที่แถวบนสุดของแผง
          ⚠ ถ้าจะเอากลับมา ต้องเอากลับมาพร้อมรูปถ่ายจริง ไม่ใช่อักษรย่อ */}
      <div className="inc-id">
        <span className="inc-who">
          <b>{who.name}</b>
          <span>
            {who.age === null ? 'ไม่ทราบอายุ' : `อายุ ${who.age} ปี`}
            {' · '}
            {p.title}
            {p.level ? ` ${levelLabel(p.level)}` : ''}
          </span>
        </span>

        {/* ⚠ ป้ายนี้บอกว่า "ผ่านเกณฑ์เบื้องต้น" ไม่ใช่ "ควรได้เลื่อน"
            เกณฑ์เต็มมีข้อที่ข้อมูลตอบไม่ได้ ดูกล่องคุณสมบัติด้านล่าง */}
        {who.promotionEligible && <span className="inc-ok">ผ่านเกณฑ์เลื่อนระดับเบื้องต้น</span>}
      </div>

      {/* ────────── เส้นทางราชการ ────────── */}
      <div className="inc-axis">
        <div className="inc-axis-hd">
          <b>เส้นทางราชการ</b>
          <span>
            {items.length > 0 ? `${items.length.toLocaleString('th-TH')} รายการ · ` : ''}
            {thaiDate(who.entryDate)} → {thaiDate(who.retireDate)}
          </span>
        </div>

        {/* หัวแกน — วันที่อัตรานี้จะว่าง */}
        <div className="inc-node is-retire">
          <span className="inc-when">
            <b>{thaiDate(who.retireDate)}</b>
            <span>{who.retireInYears === null ? 'ไม่ทราบ' : `อีก ${who.retireInYears.toFixed(1)} ปี`}</span>
          </span>
          <span className="inc-rail"><i /><s /></span>
          <span className="inc-what">
            <b>เกษียณอายุราชการ</b>
            <span>
              {who.retireFiscalYear === null
                ? 'ไม่ทราบปีงบประมาณ'
                : `ปีงบประมาณ ${who.retireFiscalYear} · อัตรานี้จะว่างลงในรอบนั้น`}
            </span>
          </span>
        </div>

        {/* ช่วงกลาง — ประวัติการดำรงตำแหน่ง */}
        {q.isPending && <p className="inc-none">กำลังโหลดประวัติ…</p>}
        {q.error && <p className="inc-none is-err">{(q.error as Error).message}</p>}
        {q.data && items.length === 0 && <p className="inc-none">ไม่มีประวัติการดำรงตำแหน่งในระบบ</p>}

        {items.length > 0 && (
          <div className={`inc-mid${scrolls ? ' is-scroll' : ''}`}>
            {items.map((it, i) => (
              <PeriodRow key={`${it.start}-${i}`} it={it} />
            ))}
          </div>
        )}

        {/* ท้ายแกน — วันเข้ารับราชการ */}
        <div className="inc-node is-entry">
          <span className="inc-when">
            <b>{thaiDate(who.entryDate)}</b>
          </span>
          <span className="inc-rail"><i /></span>
          <span className="inc-what">
            <b>เข้ารับราชการ</b>
            <span>รวมอายุราชการถึงวันนี้ {spanText(who.entryDate)}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

/** หนึ่งช่วงของการดำรงตำแหน่ง */
function PeriodRow({ it }: { it: Period }) {
  /* ⚠ ระดับมีสามทาง — ระดับตาม พ.ร.บ. 2551 · ซีเดิมก่อนปี 2551 · ระดับของบุคลากรประเภทอื่น
     ต้องเลือกทางที่มีค่า ไม่ใช่เขียน level ตรง ๆ แล้วปล่อยว่างเมื่อเป็นประวัติเก่า */
  const lv = it.level ? levelLabel(it.level) : (it.legacyLevel ?? it.nonCivilServiceLevel);

  return (
    <div className={`inc-node${it.isCurrent ? ' is-now' : ''}`}>
      <span className="inc-when">
        <b>{thaiDate(it.start)}</b>
        <span>{durationTH(it.months)}</span>
      </span>
      <span className="inc-rail"><i /><s /></span>
      <span className="inc-what">
        <span className="inc-title">
          <b>{it.title}</b>
          {it.isCurrent && <em className="inc-tag is-now">ปัจจุบัน</em>}
          {/* ⚠ รักษาการไม่ใช่การครองตำแหน่ง ต้องเขียนออกมา ไม่ใช่แสดงเหมือนกัน */}
          {it.acting && <em className="inc-tag is-act">รักษาการ</em>}
          {lv ? <em className="inc-tag">{lv}</em> : null}
        </span>
        <span>
          {thaiDate(it.start)} – {it.end ? thaiDate(it.end) : 'ปัจจุบัน'}
          {it.orderNo ? ` · คำสั่งที่ ${it.orderNo}` : ''}
        </span>
      </span>
    </div>
  );
}

/** "21 ปี 6 เดือน" นับจากวันที่ถึงวันนี้ — คืน "—" เมื่อไม่มีวัน */
function spanText(iso: string | null): string {
  if (!iso) return '—';
  const from = new Date(iso);
  if (Number.isNaN(from.getTime())) return '—';
  const now = new Date();
  const months = Math.max(
    0,
    (now.getFullYear() - from.getFullYear()) * 12 + (now.getMonth() - from.getMonth()),
  );
  return durationTH(months);
}
